import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { socialIntelligenceApi } from "./api";
import { SocialDateFilters, SocialReport } from "./types";

const defaultFilters = (): SocialDateFilters => ({
  dateFrom: moment().subtract(29, "days").startOf("day").toISOString(),
  dateTo: moment().endOf("day").toISOString(),
});
const POLLING_INTERVAL_MS = 4000;
const POLLING_TIMEOUT_MS = 2 * 60 * 1000;

export const useSocialIntelligenceCore = (
  companyId?: string | number,
  enabled = true
) => {
  const [filters, setFilters] = useState<SocialDateFilters>(defaultFilters);
  const [dashboard, setDashboard] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<SocialReport | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const polling = useRef<number | null>(null);
  const pollingStartedAt = useRef<number | null>(null);
  const activeCompanyId = useRef<string | null>(null);
  const requestGeneration = useRef(0);

  const stopPolling = useCallback(() => {
    if (polling.current) window.clearInterval(polling.current);
    polling.current = null;
    pollingStartedAt.current = null;
  }, []);

  // Nunca conservar datos de la empresa anterior mientras cambia el contexto.
  useEffect(() => {
    stopPolling();
    requestGeneration.current += 1;
    activeCompanyId.current = companyId ? String(companyId) : null;
    setDashboard(null);
    setReport(null);
    setError(null);
    setReportError(null);
  }, [companyId, enabled, stopPolling]);

  const loadDashboard = useCallback(async () => {
    if (!companyId || !enabled) {
      setDashboard(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const requestedCompanyId = String(companyId);
    const generation = requestGeneration.current;
    try {
      const [summary, sentiment, topics, opportunities, risks, jobs] =
        await Promise.all([
          socialIntelligenceApi.summary(String(companyId), filters),
          socialIntelligenceApi.sentiment(String(companyId), filters),
          socialIntelligenceApi.topics(String(companyId), filters),
          socialIntelligenceApi.opportunities(String(companyId), {
            ...filters,
            page: 1,
            pageSize: 6,
          } as SocialDateFilters),
          socialIntelligenceApi.risks(String(companyId), filters),
          socialIntelligenceApi.jobsStatus(String(companyId)),
        ]);
      if (
        activeCompanyId.current === requestedCompanyId &&
        requestGeneration.current === generation
      ) {
        setDashboard({ summary, sentiment, topics, opportunities, risks, jobs });
      }
    } catch {
      if (
        activeCompanyId.current === requestedCompanyId &&
        requestGeneration.current === generation
      ) {
        setError("No pudimos cargar la inteligencia social. Intenta nuevamente.");
      }
    } finally {
      if (
        activeCompanyId.current === requestedCompanyId &&
        requestGeneration.current === generation
      ) {
        setLoading(false);
      }
    }
  }, [companyId, enabled, filters]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(
    () => stopPolling,
    [stopPolling]
  );

  const requestExecutiveSummary = useCallback(async () => {
    if (!companyId || !enabled || polling.current) return;
    setReportError(null);
    const requestedCompanyId = String(companyId);
    const generation = requestGeneration.current;
    try {
      const result = await socialIntelligenceApi.requestSummary(
        String(companyId),
        filters
      );
      if (
        activeCompanyId.current !== requestedCompanyId ||
        requestGeneration.current !== generation
      ) {
        return;
      }
      setReport(result.report);
      pollingStartedAt.current = Date.now();
      polling.current = window.setInterval(async () => {
        if (
          !pollingStartedAt.current ||
          Date.now() - pollingStartedAt.current >= POLLING_TIMEOUT_MS
        ) {
          stopPolling();
          setReportError(
            "El resumen está tardando más de lo esperado. Puedes intentarlo nuevamente."
          );
          return;
        }
        try {
          const next = await socialIntelligenceApi.report(
            String(companyId),
            result.report.id
          );
          if (
            activeCompanyId.current !== requestedCompanyId ||
            requestGeneration.current !== generation
          ) {
            stopPolling();
            return;
          }
          setReport(next);
          if (next.status === "completed" || next.status === "failed") {
            stopPolling();
          }
        } catch {
          stopPolling();
          setReportError(
            "No pudimos consultar el estado del resumen. Intenta nuevamente."
          );
        }
      }, POLLING_INTERVAL_MS);
    } catch {
      setReportError(
        "No pudimos solicitar el resumen. Revisa la configuración e intenta nuevamente."
      );
    }
  }, [companyId, enabled, filters, stopPolling]);

  return useMemo(
    () => ({
      filters,
      setFilters,
      dashboard,
      loading,
      error,
      refresh: loadDashboard,
      report,
      reportError,
      requestExecutiveSummary,
    }),
    [
      filters,
      dashboard,
      loading,
      error,
      loadDashboard,
      report,
      reportError,
      requestExecutiveSummary,
    ]
  );
};
